// Admin Panel — Phase 2 (tabbed UI).
// Triple-gated: anonymous → login CTA; non-admin → denied; admin → 4 tabs
// (Users · Stats · Alerts · Activity) consuming the existing /api/admin/* routes.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Loader2, Bell, Users, BarChart3, AlertOctagon, Activity, Radio } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../lib/api";

import UsersTab from "./tabs/UsersTab";
import StatsTab from "./tabs/StatsTab";
import AlertsTab from "./tabs/AlertsTab";
import ActivityTab from "./tabs/ActivityTab";
import OpsTab from "./tabs/OpsTab";

const ADMIN_EMAILS = new Set([
  "rebelbtc@rebelbtc.com",
  "camilorodriguezgil9@outlook.es",
]);

const TABS = [
  { key: "stats",    label: "Stats",    Icon: BarChart3,   Component: StatsTab },
  { key: "users",    label: "Users",    Icon: Users,       Component: UsersTab },
  { key: "alerts",   label: "Alerts",   Icon: AlertOctagon, Component: AlertsTab },
  { key: "activity", label: "Activity", Icon: Activity,    Component: ActivityTab },
  { key: "ops",      label: "Ops",      Icon: Radio,       Component: OpsTab },
];

export default function AdminPanelPage() {
  const { user, token, loading, openLogin } = useAuth();
  const [active, setActive] = useState("stats");
  const [webhookTesting, setWebhookTesting] = useState(false);

  const userEmail = (user?.email || "").toLowerCase();
  const isAdmin = !loading && user && ADMIN_EMAILS.has(userEmail);

  const testWebhook = async () => {
    if (!token) return;
    setWebhookTesting(true);
    try {
      const r = await fetch(`${API}/admin/security/webhook-test`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.clone().json();
      toast(d.ok ? `✅ Webhook ${d.type} disparado` : "⚠️ Webhook no configurado", {
        description: d.message, duration: d.ok ? 5000 : 7000,
      });
    } catch (e) {
      toast("❌ Error probando webhook", { description: String(e), duration: 5000 });
    } finally {
      setWebhookTesting(false);
    }
  };

  // Gate: not authenticated.
  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-[#080508] px-6 py-16 text-white">
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 text-center">
          <ShieldAlert className="h-10 w-10 text-[#f97316]" />
          <h1 className="font-mono text-lg font-bold uppercase tracking-[3px] text-[#f97316]">
            Admin Panel
          </h1>
          <p className="font-mono text-xs text-white/55">
            Esta zona requiere autenticación administrativa.
          </p>
          <button
            type="button"
            onClick={openLogin}
            data-testid="admin-login-cta"
            className="rounded-full border border-[#f97316] bg-[#f97316]/15 px-5 py-2 font-mono text-[11px] font-bold uppercase tracking-[2px] text-[#f97316] hover:bg-[#f97316]/25"
          >
            Acceder
          </button>
          <Link to="/" className="font-mono text-[10px] text-white/40 hover:text-white">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  // Gate: authenticated but not in the allowlist.
  if (!loading && user && !isAdmin) {
    return (
      <div className="min-h-screen bg-[#080508] px-6 py-16 text-white">
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 text-center">
          <ShieldAlert className="h-10 w-10 text-[#ff3b5c]" />
          <h1 className="font-mono text-lg font-bold uppercase tracking-[3px]" style={{ color: "#ff3b5c" }}>
            Acceso denegado
          </h1>
          <p className="font-mono text-xs text-white/55">
            Tu cuenta no tiene permisos para ver el panel administrativo.
          </p>
          <Link
            to="/"
            data-testid="admin-back-home"
            className="rounded-full border border-white/20 bg-white/[0.04] px-5 py-2 font-mono text-[11px] font-bold uppercase tracking-[2px] text-white/80 hover:border-white/40"
          >
            <ArrowLeft className="mr-1 inline h-3 w-3" /> Volver
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#080508] text-white/55">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const ActiveComponent = TABS.find((t) => t.key === active)?.Component || StatsTab;

  return (
    <div className="min-h-screen bg-[#080508] px-4 py-10 text-white sm:px-6 sm:py-12" data-testid="admin-panel-page">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/35">RE₿EL · ADMIN</p>
            <h1 className="mt-1 font-mono text-2xl font-bold tracking-wider text-[#f97316]">
              👑 Admin Panel
            </h1>
            <p className="mt-1 font-mono text-[10px] text-white/45">
              Sesión: <span className="text-white/80">{userEmail}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={testWebhook}
              disabled={webhookTesting}
              data-testid="admin-webhook-test-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#f97316]/35 bg-[#f97316]/[0.08] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[1.5px] text-[#f97316] transition-all hover:bg-[#f97316]/[0.15] disabled:opacity-50"
            >
              {webhookTesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bell className="h-3 w-3" />}
              Probar webhook
            </button>
            <Link
              to="/"
              data-testid="admin-back-link"
              className="rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[2px] text-white/70 hover:border-white/40 hover:text-white"
            >
              <ArrowLeft className="mr-1 inline h-3 w-3" /> Inicio
            </Link>
          </div>
        </div>

        {/* Tab bar */}
        <div
          className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/10 bg-white/[0.02] p-1"
          data-testid="admin-tab-bar"
        >
          {TABS.map(({ key, label, Icon }) => {
            const isActive = active === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                data-testid={`admin-tab-btn-${key}`}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[1.5px] transition-all"
                style={isActive
                  ? { background: "rgba(249,115,22,0.18)", color: "#f97316" }
                  : { background: "transparent", color: "rgba(255,255,255,0.55)" }}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            );
          })}
        </div>

        {/* Active tab content */}
        <div className="rounded-xl border border-white/10 bg-white/[0.01] p-4">
          <ActiveComponent token={token} />
        </div>

        <p className="mt-6 text-center font-mono text-[9px] text-white/30">
          Endpoints: <code>/api/admin/users</code> · <code>/stats</code> ·{" "}
          <code>/security/alerts</code> · <code>/activity-log</code>
        </p>
      </div>
    </div>
  );
}
